import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { templateKeys } from './notification.options';
import NotificationService from './notification.service';

type ActionType = 'createTemplate' | 'modifyTemplate' | 'deleteTemplate';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

const useTicketPrint = (): {
  createTemplate: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  modifyTemplate: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  deleteTemplate: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  isUpdatingTemplate: Record<string, boolean>;
  setIsUpdatingTemplate: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingTemplate, setIsUpdatingTemplate] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const notificationService = NotificationService();
    const action: { [key in ActionType]?: Function } = {
      createTemplate: notificationService.postNewTemplate,
      modifyTemplate: notificationService.postModifyTemplate,
      deleteTemplate: notificationService.deleteTemplate,
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  const { mutateAsync: createTemplate } = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType }) => {
      setIsUpdatingTemplate({ ...isUpdatingTemplate, [actionType]: true });
    },
    onSuccess: (data: any, variables: any) => {
      // console.log('template create onSuccess:', data, variables);

      if (data?.status === 200 && variables.actionType === 'createTemplate') {
        enqueueSnackbar('New Template saved successfully', { variant: 'success' });
      } else if (data?.status !== 200 && variables.actionType === 'createTemplate') {
        enqueueSnackbar('New Template save failed', { variant: 'error' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during Template creation:', error);

      if (variables.actionType === 'createTemplate') {
        enqueueSnackbar('New Template save failed', { variant: 'error' });
      }
    },
    onSettled: ({ actionType }) => {
      setIsUpdatingTemplate({ ...isUpdatingTemplate, [actionType]: false });
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });

  const { mutateAsync: modifyTemplate } = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType }) => {
      setIsUpdatingTemplate({ ...isUpdatingTemplate, [actionType]: true });
    },
    onSuccess: (data: any, variables: any) => {
      // console.log('template create onSuccess:', data, variables);

      if (data?.status === 200 && variables.actionType === 'modifyTemplate') {
        enqueueSnackbar('New Template saved successfully', { variant: 'success' });
      } else if (data?.status !== 200 && variables.actionType === 'modifyTemplate') {
        enqueueSnackbar('New Template save failed', { variant: 'error' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during Template creation:', error);

      if (variables.actionType === 'modifyTemplate') {
        enqueueSnackbar('New Template save failed', { variant: 'error' });
      }
    },
    onSettled: ({ actionType, data }) => {
      setIsUpdatingTemplate({ ...isUpdatingTemplate, [actionType]: false });
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
      queryClient.invalidateQueries({ queryKey: ['download-template-by-template-id', data?.id] });

    },
  });

  const { mutateAsync: deleteTemplate } = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType }) => {
      setIsUpdatingTemplate({ ...isUpdatingTemplate, [actionType]: true });
    },
    onSuccess: (data: any, variables: any) => {
      console.log('template delete onSuccess data:', data);
      console.log('template delete onSuccess variables:', variables);

      if (variables.actionType === 'deleteTemplate') {
        enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      } else if (variables.actionType === 'deleteTemplate') {
        enqueueSnackbar('Template delete failed', { variant: 'error' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during Template deletion:', error);

      if (variables.actionType === 'deleteTemplate') {
        enqueueSnackbar('Template delete failed', { variant: 'error' });
      }
    },
    onSettled: ({ actionType }) => {
      setIsUpdatingTemplate({ ...isUpdatingTemplate, [actionType]: false });
      queryClient.invalidateQueries({ queryKey: templateKeys.all });
    },
  });

  return { createTemplate, modifyTemplate, deleteTemplate, isUpdatingTemplate, setIsUpdatingTemplate };
};

export default useTicketPrint;
